const FORM_SELECTOR = "form[data-candidates-filter-form]";

let bound = false;

function filterForm(): HTMLFormElement | null {
  return document.querySelector<HTMLFormElement>(FORM_SELECTOR);
}

function submitForm(): void {
  const form = filterForm();
  if (!form) return;
  if (typeof form.requestSubmit === "function") form.requestSubmit();
  else form.submit();
}

function allDds(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>("[data-cand-dd]"));
}

function specificInputs(dd: HTMLElement): HTMLInputElement[] {
  return Array.from(dd.querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]'))
    .filter((i) => i.value !== "" && !i.hasAttribute("data-dd-master"));
}

function masterOf(dd: HTMLElement): HTMLInputElement | null {
  return dd.querySelector<HTMLInputElement>("[data-dd-master]");
}

// reflect the current selection onto the visuals + trigger label
function refresh(dd: HTMLElement): void {
  const single = dd.hasAttribute("data-single");
  const placeholder = dd.dataset.placeholder || "All";
  const trigger = dd.querySelector<HTMLElement>("[data-dd-trigger]");
  const label = dd.querySelector<HTMLElement>(".dd-label");
  const allOpt = dd.querySelector<HTMLElement>("[data-dd-all]");
  const specific = specificInputs(dd);
  const sel = specific.filter((i) => i.checked);
  const master = masterOf(dd);
  const all = specific.length > 0 && sel.length === specific.length;

  specific.forEach((i) => i.closest<HTMLElement>(".dd-opt")?.classList.toggle("active", i.checked));

  if (master) {
    master.checked = all;
    master.indeterminate = sel.length > 0 && !all; // 1..N-1 selected
    allOpt?.classList.toggle("active", all);
    allOpt?.classList.toggle("indet", master.indeterminate);
  } else {
    allOpt?.classList.toggle("active", sel.length === 0);
  }

  // A fully-ticked master group filters nothing (its params are dropped on
  // submit), so it presents as the cleared state rather than "N selected".
  const constrained = sel.length > 0 && !(master && all);
  trigger?.classList.toggle("has-val", constrained);
  if (!label) return;
  if (!constrained) label.textContent = placeholder;
  else if (single || sel.length === 1) {
    label.textContent = sel[0].closest<HTMLElement>(".dd-opt")?.dataset.label || placeholder;
  } else label.textContent = `${sel.length} selected`;
}

function setAll(dd: HTMLElement, checked: boolean): void {
  specificInputs(dd).forEach((i) => { i.checked = checked; });
  refresh(dd);
  dd.dataset.dirty = "true";
}

function clearFilter(dd: HTMLElement): void {
  specificInputs(dd).forEach((i) => { i.checked = false; });
  const allRadio = dd.querySelector<HTMLInputElement>('[data-dd-all] input[type="radio"]');
  if (allRadio) allRadio.checked = true; // single: (re)check the empty radio
  refresh(dd);
  dd.dataset.dirty = "true";
}

function close(dd: HTMLElement): void {
  if (!dd.classList.contains("open")) return;
  dd.classList.remove("open");
  dd.querySelector<HTMLElement>("[data-dd-trigger]")?.setAttribute("aria-expanded", "false");
  if (dd.dataset.dirty === "true") {
    dd.dataset.dirty = "false";
    submitForm(); // triggers full navigation → fresh table + toolbar
  }
}

function closeAll(except?: HTMLElement): void {
  allDds().forEach((dd) => { if (dd !== except) close(dd); });
}

function applySearch(dd: HTMLElement): void {
  const search = dd.querySelector<HTMLInputElement>("[data-dd-search]");
  const terms = String(search?.value || "").toLowerCase().split(/\s+/).filter(Boolean);
  const empty = dd.querySelector<HTMLElement>("[data-dd-empty]");
  let visible = 0;

  dd.querySelectorAll<HTMLElement>(".dd-opt").forEach((opt) => {
    if (opt.hasAttribute("data-dd-all")) { opt.style.display = ""; return; }
    const hay = (opt.dataset.label || opt.textContent || "").toLowerCase();
    const show = terms.every((term) => hay.includes(term));
    opt.style.display = show ? "" : "none";
    if (show) visible++;
  });
  if (empty) empty.style.display = visible ? "none" : "";
}

/* Remote (searched + paged) option lists ---------------------------------- */

const SEARCH_DEBOUNCE_MS = 250;
const SCROLL_THRESHOLD_PX = 120;

interface RemoteState { page: number; hasMore: boolean; loading: boolean; query: string; seq: number }

const remote = new WeakMap<HTMLElement, RemoteState>();
const debounces = new WeakMap<HTMLElement, number>();
const scrollBound = new WeakSet<HTMLElement>();

const isRemote = (dd: HTMLElement): boolean => Boolean(dd.dataset.ddUrl);

function stateOf(dd: HTMLElement): RemoteState {
  let state = remote.get(dd);
  if (!state) {
    // the server rendered page 1; has-more says whether there is anything past it
    state = { page: 1, hasMore: dd.dataset.ddHasMore === "true", loading: false, query: "", seq: 0 };
    remote.set(dd, state);
  }
  return state;
}

function optionRow(dd: HTMLElement, label: string, value: string): HTMLLabelElement {
  const row = document.createElement("label");
  row.className = "dd-opt";
  row.dataset.label = label;
  const input = document.createElement("input");
  input.type = dd.hasAttribute("data-single") ? "radio" : "checkbox";
  input.name = dd.dataset.ddName || "";
  input.value = value;
  const tick = document.createElement("span");
  tick.className = "tick";
  row.append(input, tick, document.createTextNode(label));
  return row;
}

// A value already on the page must not be rendered twice — the server strips the
// server-known selection, but anything ticked in this session it cannot know about.
function renderedValues(dd: HTMLElement): Set<string> {
  return new Set(Array.from(dd.querySelectorAll<HTMLInputElement>(".dd-opt input")).map((i) => i.value));
}

// "No matches" reports on the search, so it tracks the fetched window only —
// the pinned selection stays listed whether or not it matches.
function refreshEmptyState(dd: HTMLElement): void {
  const empty = dd.querySelector<HTMLElement>("[data-dd-empty]");
  const win = dd.querySelector<HTMLElement>("[data-dd-window]");
  if (empty) empty.style.display = win && win.childElementCount ? "none" : "";
}

async function loadOptions(dd: HTMLElement, reset: boolean): Promise<void> {
  const state = stateOf(dd);
  // a new search always supersedes; only paging waits its turn
  if (!reset && (state.loading || !state.hasMore)) return;

  const page = reset ? 1 : state.page + 1;
  const url = new URL(dd.dataset.ddUrl as string, window.location.origin);
  url.searchParams.set("q", state.query);
  url.searchParams.set("page", String(page));

  const seq = ++state.seq;
  state.loading = true;
  const spinner = dd.querySelector<HTMLElement>("[data-dd-loading]");
  if (spinner) spinner.style.display = "";

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    const data = await response.json();
    if (seq !== state.seq) return; // a newer keystroke already went out — drop this

    const win = dd.querySelector<HTMLElement>("[data-dd-window]");
    if (!win) return;
    if (reset) win.textContent = "";
    const seen = renderedValues(dd);
    (data.options || []).forEach((pair: [string, string]) => {
      const [label, value] = [String(pair[0]), String(pair[1])];
      if (seen.has(value)) return;
      seen.add(value);
      win.appendChild(optionRow(dd, label, value));
    });
    state.page = page;
    state.hasMore = Boolean(data.has_more);
    refreshEmptyState(dd);
  } catch {
    /* keep whatever is already rendered */
  } finally {
    if (seq === state.seq) {
      state.loading = false;
      if (spinner) spinner.style.display = "none";
    }
  }
}

function bindScroll(dd: HTMLElement): void {
  const opts = dd.querySelector<HTMLElement>("[data-dd-opts]");
  if (!opts || scrollBound.has(opts)) return;
  scrollBound.add(opts);
  opts.addEventListener("scroll", () => {
    if (opts.scrollTop + opts.clientHeight >= opts.scrollHeight - SCROLL_THRESHOLD_PX) loadOptions(dd, false);
  });
}

// Ticking an option in the fetched window moves it into the pinned block, so the
// next search (which replaces that window) cannot destroy a live selection.
function pinIfSelected(dd: HTMLElement, input: HTMLInputElement): void {
  if (!isRemote(dd) || !input.checked) return;
  const pinned = dd.querySelector<HTMLElement>("[data-dd-pinned]");
  const row = input.closest<HTMLElement>(".dd-opt");
  if (pinned && row && !pinned.contains(row)) pinned.appendChild(row);
}

export function initCandidatesFilters(): void {
  // `indeterminate` cannot be expressed in markup, so re-derive the master state
  // from the rendered checkboxes on every render, not just the first.
  allDds().forEach((dd) => { if (masterOf(dd)) refresh(dd); });

  if (bound) return;
  bound = true;

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dd = target.closest<HTMLElement>("[data-cand-dd]");

    // outside click closes any open dropdown (submitting it if changed)
    if (!dd) { closeAll(); return; }

    const trigger = target.closest<HTMLElement>("[data-dd-trigger]");
    if (trigger) {
      const willOpen = !dd.classList.contains("open");
      closeAll(dd); // closing a dirty sibling submits → navigation
      if (!willOpen) { close(dd); return; }
      dd.classList.add("open");
      trigger.setAttribute("aria-expanded", "true");
      const search = dd.querySelector<HTMLInputElement>("[data-dd-search]");
      if (search) {
        search.value = "";
        if (isRemote(dd)) {
          bindScroll(dd);
          const state = stateOf(dd);
          // reopening drops a search left over from last time — refetch page 1
          if (state.query) { state.query = ""; loadOptions(dd, true); }
        } else applySearch(dd);
        window.requestAnimationFrame(() => search.focus());
      }
      return;
    }

    const allOpt = target.closest<HTMLElement>("[data-dd-all]");
    // with a master checkbox the row is a real control — the change listener owns it
    if (allOpt && !dd.hasAttribute("data-single") && !masterOf(dd)) clearFilter(dd);
  });

  document.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== "checkbox" && input.type !== "radio") return;
    const dd = input.closest<HTMLElement>("[data-cand-dd]");
    if (!dd) return;

    const single = dd.hasAttribute("data-single");
    // master: select-all / deselect-all (an indeterminate box checks on click)
    if (input.hasAttribute("data-dd-master")) { setAll(dd, input.checked); return; }
    if (input.value === "") { clearFilter(dd); if (single) close(dd); return; } // single "All"
    pinIfSelected(dd, input);
    refresh(dd);
    dd.dataset.dirty = "true";
    if (single) close(dd); // single-select applies immediately
  });

  document.addEventListener("input", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-dd-search")) return;
    const dd = input.closest<HTMLElement>("[data-cand-dd]");
    if (!dd) return;
    if (!isRemote(dd)) { applySearch(dd); return; }

    stateOf(dd).query = input.value;
    window.clearTimeout(debounces.get(dd));
    debounces.set(dd, window.setTimeout(() => loadOptions(dd, true), SEARCH_DEBOUNCE_MS));
  });

  // Enter inside the option search must not submit the surrounding GET filter form
  document.addEventListener("keydown", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-dd-search")) return;
    if ((event as KeyboardEvent).key === "Enter") event.preventDefault();
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    allDds().forEach((dd) => {
      if (!masterOf(dd)?.checked) return;
      specificInputs(dd).forEach((i) => { i.disabled = true; });
    });

    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement;
      if (!input.name || input.disabled) return;
      if ((input.type === "checkbox" || input.type === "radio") && !input.checked) return;
      if (input.value === "") input.disabled = true;
    });
  });
}
